import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { X, AlertTriangle, ChevronLeft } from "lucide-react";
import { toast } from "react-toastify";
import { useAppSelector } from "../../../store/hooks";
import conferenceAndTradeMissionService from "../../../api/ConferenceAndTradeMission";
import RegistrationLayout from "./RegistrationLayout";
import StepAgentReferral from "./StepAgentReferral";
import Step2PersonalDetails from "./Step2PersonalDetails";
import Step3ContactDetails from "./Step3ContactDetails";
import Step4MotivationQuestions from "./Step4MotivationQuestions";
import StepPaymentChoice from "./StepPaymentChoice";
import generalService from "../../../api/GeneralService";
import { normalizeContactNumber, validateContactNumber } from "../../../utils/formValidators";

const GENERIC_PROFILE_PHOTO_BASE64 =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAusB9WlH0xkAAAAASUVORK5CYII=";

export default function ConferenceRegistration({ onBackToTypeSelection }) {
  const navigate = useNavigate();
  const { slug } = useParams();
  const location = useLocation();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const user = useAppSelector((state) => state.auth.user);
  const [currentStep, setCurrentStep] = useState(0); // Start at 0 for referral step
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    hasReferralCode: null,
    agentReferralCode: "",
    validatedReferralCode: false,
    agentDetails: null,
    firstName: "",
    lastName: "",
    nationality: "",
    email: "",
    gender: "",
    dateOfBirth: "",
    profession: "",
    organization: "",
    countryOfResidence: "",
    departureCity: "",
    profilePhoto: null,
    contactNumber: "",
    address: "",
    motivationAnswers: [],
    selectedTicket: null,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [showPaymentChoice, setShowPaymentChoice] = useState(false);
  const [referralValidationState, setReferralValidationState] = useState({ isValidating: false, isValid: false });
  const [showExitModal, setShowExitModal] = useState(false);

  const isFreeTicket = Number(parseFloat(formData.selectedTicket?.price ?? 0)) === 0;
  const profileGender =
    user?.gender ||
    user?.sex ||
    user?.user_gender ||
    "";
  const profileDateOfBirth =
    user?.date_of_birth ||
    user?.dateOfBirth ||
    user?.dob ||
    user?.birth_date ||
    "";
  const profileCountryOfResidence =
    user?.country_of_residence ||
    user?.countryOfResidence ||
    user?.country ||
    user?.nationality_country ||
    "Ghana";

  // Never show Pay now / Pay later for free tickets: redirect to success if we ever would
  useEffect(() => {
    if (showPaymentChoice && isFreeTicket && registrationData) {
      navigate(`/event-details/${slug}/registration-success`, {
        state: {
          applicationId: slug,
          registrationId: registrationData?.id || registrationData?.registration_id,
          registrationData,
          paymentVerified: true,
          isFreeTicket: true,
        },
      });
    }
  }, [showPaymentChoice, isFreeTicket, registrationData, navigate, slug]);

  // Conferences must have a ticket/participation category selected before entering registration
  useEffect(() => {
    let ticketFromState = location.state?.selectedTicket || null;

    if (!ticketFromState && slug) {
      try {
        const stored = sessionStorage.getItem(`conferenceRegistrationTicket_${slug}`);
        if (stored) ticketFromState = JSON.parse(stored);
      } catch {
        // ignore parse/storage failures
      }
    }

    if (!ticketFromState) {
      toast.info("Please select a ticket category to continue");
      navigate(`/event-details/${slug}`);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      selectedTicket: ticketFromState,
    }));
  }, [location.state, navigate, slug]);

  useEffect(() => {
    if (!isAuthenticated) {
      toast.info("Please sign in to register for this event");
      navigate("/signin", {
        state: { from: `/event-details/${slug}/register` },
      });
    }
  }, [isAuthenticated, navigate, slug]);

  useEffect(() => {
    if (!user) return;
    setFormData((prev) => ({
      ...prev,
      firstName: user?.first_name || user?.firstName || prev.firstName || "",
      lastName: user?.last_name || user?.lastName || prev.lastName || "",
      email: user?.email || prev.email || "",
      nationality: user?.nationality || prev.nationality || "",
      gender: profileGender || prev.gender || "",
      dateOfBirth: profileDateOfBirth || prev.dateOfBirth || "",
      countryOfResidence:
        profileCountryOfResidence ||
        prev.countryOfResidence ||
        "Ghana",
    }));
  }, [user, profileCountryOfResidence, profileDateOfBirth, profileGender]);

  useEffect(() => {
    const fetchEventData = async () => {
      if (!slug) return;
      setLoading(true);
      try {
        const response = await conferenceAndTradeMissionService.getEventDetails(slug);
        if (response.data?.status_code === 200 && response.data?.data?.event) {
          setEventData(response.data.data.event);
        } else {
          toast.error("Failed to load event details");
          navigate(`/event-details/${slug}`);
        }
      } catch (error) {
        toast.error("An error occurred while loading event details");
        navigate(`/event-details/${slug}`);
      } finally {
        setLoading(false);
      }
    };
    fetchEventData();
  }, [slug, navigate]);

  // API function to validate agent referral code
  const  validateAgentReferral = async (code) => {
    try {
      const response = await generalService.validateAgentReferralCode(code);
      
      if (response.data?.status_code === 200) {
        return {
          success: true,
          message: response.data.reason || "Referral code verified successfully!",
          agentDetails: response.data.data?.agent,
        };
      } else {
        return {
          success: false,
          message: response.data?.reason || "Invalid referral code",
        };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.data?.reason || "An error occurred while validating agent code",
      };
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (formData.hasReferralCode === null) {
        newErrors.hasReferralCode = "Please indicate if you have a referral code";
      } else if (formData.hasReferralCode === true) {
        if (!formData.agentReferralCode?.trim()) {
          newErrors.agentReferralCode = "Please enter your referral code";
        } else if (!formData.validatedReferralCode) {
          newErrors.agentReferralCode = "Please verify your referral code before proceeding";
        }
      }
    }

    if (step === 1) {
      const genderValue = (formData.gender || profileGender || "").trim();
      const dateOfBirthValue = (formData.dateOfBirth || profileDateOfBirth || "").trim();
      const countryOfResidenceValue =
        (formData.countryOfResidence || profileCountryOfResidence || "").trim() ||
        "Ghana";
      if (!genderValue) newErrors.gender = "Gender is required";
      if (!dateOfBirthValue) newErrors.dateOfBirth = "Date of birth is required";
      if (!formData.profession?.trim()) newErrors.profession = "Profession is required";
      if (!formData.organization?.trim()) newErrors.organization = "Organization/University is required";
      if (!countryOfResidenceValue) newErrors.countryOfResidence = "Country of residence is required";
      if (!formData.departureCity?.trim()) newErrors.departureCity = "Departure city is required";
    }

    if (step === 2) {
      const contactValidation = validateContactNumber(formData.contactNumber);
      if (!formData.contactNumber?.trim()) {
        newErrors.contactNumber = "Contact number is required";
      } else if (!contactValidation.isValid) {
        newErrors.contactNumber = contactValidation.message;
      }
      if (!formData.address?.trim()) newErrors.address = "Address is required";
    }

    if (step === 3) {
      const motivationalQuestions = eventData?.motivational_questions || [];
      
      // Check if it's the new format (sections) or old format (flat questions)
      const isNewFormat = motivationalQuestions.length > 0 && motivationalQuestions[0]?.sectionTitle !== undefined;
      
      if (isNewFormat) {
        // New format with sections
        motivationalQuestions.forEach((section, sectionIndex) => {
          section.questions?.forEach((question, questionIndex) => {
            if (question.required) {
              const answer = formData.motivationAnswers?.[sectionIndex]?.[questionIndex];
              if (!answer || (typeof answer === "string" && !answer.trim()) || (Array.isArray(answer) && answer.length === 0)) {
                newErrors[`motivation_${sectionIndex}_${questionIndex}`] = "This question is required";
              }
            }
          });
        });
      } else {
        // Old format - backward compatibility
        motivationalQuestions.forEach((question, index) => {
          const answer = formData.motivationAnswers?.[index];
          if (!answer || (typeof answer === "string" && !answer.trim())) {
            newErrors[`motivation_${index}`] = "This question is required";
          }
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      const hasMotivationQuestions = eventData?.motivational_questions?.length > 0;
      const finalStep = hasMotivationQuestions ? 3 : 2;
      
      if (currentStep === finalStep && !showPaymentChoice) {
        handleFormSubmit();
      } else {
        setCurrentStep(currentStep + 1);
      }
    } else {
      toast.error("Please fill in all required fields");
    }
  };

  const handleBack = () => {
    if (currentStep === 0 && onBackToTypeSelection) {
      onBackToTypeSelection();
      return;
    }
    if (currentStep > 0 && !showPaymentChoice) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  // Helper function to convert File to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const transformFormDataToPayload = async () => {
    if (!formData.selectedTicket) {
      toast.info("Please select a ticket category to continue");
      navigate(`/event-details/${slug}`);
      throw new Error("Missing selectedTicket for conference registration");
    }

    // Convert profile photo to base64 if it exists
    let profilePhotoBase64 = null;
    if (formData.profilePhoto instanceof File) {
      try {
        profilePhotoBase64 = await fileToBase64(formData.profilePhoto);
      } catch (error) {
        console.error("Error converting profile photo to base64:", error);
        toast.error("Failed to process profile photo. Please try again.");
        throw error;
      }
    } else if (typeof formData.profilePhoto === 'string') {
      // If it's already a base64 string, use it as is
      profilePhotoBase64 = formData.profilePhoto;
    }
    if (!profilePhotoBase64) {
      profilePhotoBase64 = GENERIC_PROFILE_PHOTO_BASE64;
    }

    const clientSlug = user?.slug || user?.client_slug;
    if (!clientSlug) {
      toast.error("Your account session is missing. Please sign in again.");
      throw new Error("client_slug is required for registration");
    }

    const countryFallback = profileCountryOfResidence || "Ghana";
    const nationalityFromUser = user?.nationality || countryFallback || "Ghana";
    const firstNameFromUser = user?.first_name || user?.firstName || "";
    const lastNameFromUser = user?.last_name || user?.lastName || "";
    const emailFromUser = user?.email || "";
    const genderFromUser = profileGender || "";
    const dateOfBirthFromUser = profileDateOfBirth || "";
    const countryOfResidenceFromUser = profileCountryOfResidence || "Ghana";
    const effectiveGender = formData.gender || genderFromUser;
    const effectiveDateOfBirth = formData.dateOfBirth || dateOfBirthFromUser;
    const effectiveCountryOfResidence = formData.countryOfResidence || countryOfResidenceFromUser || "Ghana";

    if (!firstNameFromUser || !lastNameFromUser || !emailFromUser) {
      toast.error("Your profile details are incomplete. Please update your account and try again.");
      throw new Error("Missing required basic profile information from auth user");
    }
    if (!effectiveGender || !effectiveDateOfBirth) {
      toast.error("Your profile is missing gender or date of birth. Please update your account and try again.");
      throw new Error("Missing required gender/date_of_birth profile information from auth user");
    }

    const payload = {
      client_slug: clientSlug,
      event_slug: slug,
      event_type: "conference",
      registration_type: "single",
      // Backend expects number_of_attendees even for single registrations
      number_of_attendees: 1,
      // Store full ticket/participation category details as an object
      ticket_type: formData.selectedTicket,
      first_name: firstNameFromUser,
      last_name: lastNameFromUser,
      nationality: nationalityFromUser,
      email: emailFromUser,
      gender: effectiveGender,
      date_of_birth: effectiveDateOfBirth,
      profession: formData.profession,
      organization: formData.organization,
      country_of_residence: effectiveCountryOfResidence,
      departure_city: formData.departureCity,
      contact_number: normalizeContactNumber(formData.contactNumber),
      address: formData.address,
      profile_photo: profilePhotoBase64,
    };

    if (formData.hasReferralCode && formData.agentReferralCode && formData.validatedReferralCode) {
      payload.agent_code = formData.agentReferralCode;
    }

    const motivationalQuestions = eventData?.motivational_questions || [];
    if (motivationalQuestions.length > 0 && formData.motivationAnswers) {
      // Check if it's the new format (sections) or old format (flat questions)
      const isNewFormat = motivationalQuestions.length > 0 && motivationalQuestions[0]?.sectionTitle !== undefined;
      
      if (isNewFormat) {
        // New format with sections
        const answers = [];
        motivationalQuestions.forEach((section, sectionIndex) => {
          section.questions?.forEach((question, questionIndex) => {
            const answer = formData.motivationAnswers[sectionIndex]?.[questionIndex];
            if (answer !== undefined && answer !== null) {
              answers.push({
                section: section.sectionTitle,
                question: question.question,
                answer: answer,
                type: question.type,
              });
            }
          });
        });
        payload.motivation_answers = answers;
      } else {
        // Old format - backward compatibility
        payload.motivation_answers = motivationalQuestions.map((question, index) => ({
          question: question.question,
          answer: formData.motivationAnswers[index],
        }));
      }
    }

    return payload;
  };

  const handleFormSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = await transformFormDataToPayload();
      
      const jsonPayload = {
        ...payload,
        motivation_answers: payload.motivation_answers || [],
      };

      const response = await conferenceAndTradeMissionService.registerTradeMission(jsonPayload);

      if (response.data?.status_code === 200) {
        const registrationInfo = response.data.data;
        setRegistrationData(registrationInfo);
        
        // Free/0 amount ticket: skip payment, go straight to success
        const selectedTicketPrice = Number(parseFloat(formData.selectedTicket?.price ?? 0));
        const isFreeTicket = selectedTicketPrice === 0;

        if (isFreeTicket) {
          // Free ticket - skip payment choice, go directly to success
          toast.success(response.data.reason || "Registration completed successfully!");
          navigate(`/event-details/${slug}/registration-success`, {
            state: {
              applicationId: slug,
              registrationId: registrationInfo?.id || registrationInfo?.registration_id,
              registrationData: registrationInfo,
              paymentVerified: true, // Free ticket = no payment needed
              isFreeTicket: true,
            },
          });
        } else {
          // Paid ticket - show payment choice
          toast.success(response.data.reason || "Registration submitted successfully!");
          setShowPaymentChoice(true);
          const hasMotivationQuestions = eventData?.motivational_questions?.length > 0;
          setCurrentStep(hasMotivationQuestions ? 5 : 4);
        }
        setIsSubmitting(false);
      } else {
        const responseReason =
          response.data?.reason ||
          response.data?.data?.reason ||
          response.data?.data?.message ||
          response.data?.message ||
          response.message;
        toast.error(responseReason || "Failed to submit registration. Please try again.");
        setIsSubmitting(false);
      }
    } catch (error) {
      const errorReason =
        error.response?.data?.reason ||
        error.response?.data?.message ||
        error.response?.data?.data?.reason ||
        error.response?.data?.data?.message ||
        error?.message;
      toast.error(errorReason || "An unexpected error occurred. Please try again.");
      setIsSubmitting(false);
    }
  };

  const handlePayLater = () => {
    navigate(`/event-details/${slug}/registration-success`, {
      state: {
        applicationId: slug,
        registrationId: registrationData?.id || registrationData?.registration_id,
        registrationData: registrationData,
        eventData,
        selectedTicket: formData.selectedTicket,
        paymentVerified: false,
        payLater: true,
      },
    });
  };

  const handlePayNow = () => {
    navigate(`/event-details/${slug}/payment`, {
      state: {
        registrationData: registrationData,
        eventData: eventData,
        slug: slug,
        selectedTicket: formData.selectedTicket,
      },
    });
  };

  const handleClose = () => {
    if (currentStep > 0 || showPaymentChoice) {
      setShowExitModal(true);
    } else {
      navigate(`/event-details/${slug}`);
    }
  };

  const handleConfirmExit = () => {
    setShowExitModal(false);
    navigate(`/event-details/${slug}`);
  };

  const handleCancelExit = () => {
    setShowExitModal(false);
  };

  const renderStep = () => {
    const hasMotivationQuestions = eventData?.motivational_questions?.length > 0;

    // Do not show Pay now / Pay later for free tickets
    if (showPaymentChoice && !isFreeTicket) {
      return (
        <StepPaymentChoice
          formData={formData}
          setFormData={setFormData}
          onPayNow={handlePayNow}
          onPayLater={handlePayLater}
        />
      );
    }
    if (showPaymentChoice && isFreeTicket) {
      return (
        <div className="flex flex-col justify-center items-center py-12 text-center">
          <p className="text-gray-600">Redirecting to confirmation...</p>
        </div>
      );
    }

    switch (currentStep) {
      case 0:
        return (
          <StepAgentReferral
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            onValidateReferral={validateAgentReferral}
            onValidationStateChange={setReferralValidationState}
          />
        );
      case 1:
        return (
          <Step2PersonalDetails
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            showProfilePhoto={false}
          />
        );
      case 2:
        return (
          <Step3ContactDetails
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      case 3:
        if (hasMotivationQuestions) {
          return (
            <Step4MotivationQuestions
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              motivationalQuestions={eventData.motivational_questions}
            />
          );
        }
        return null;
      default:
        return null;
    }
  };

  const hasMotivationQuestions = eventData?.motivational_questions?.length > 0;
  const paymentStepNumber = hasMotivationQuestions ? 4 : 3;
  const displayStep = showPaymentChoice ? paymentStepNumber : currentStep;

  const steps = useMemo(() => {
    const list = [
      { number: 0, label: "Agent Referral", shortLabel: "Referral" },
      { number: 1, label: "Personal Details", shortLabel: "Personal" },
      { number: 2, label: "Contact Details", shortLabel: "Contact" },
    ];
    if (hasMotivationQuestions) {
      list.push({ number: 3, label: "Motivation Questions", shortLabel: "Additional Information" });
    }
    list.push({ number: paymentStepNumber, label: "Payment", shortLabel: "Payment" });
    return list;
  }, [hasMotivationQuestions, paymentStepNumber]);

  const handleStepClick = (stepNumber) => {
    if (showPaymentChoice) return;
    if (stepNumber <= currentStep) setCurrentStep(stepNumber);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-orange-200 animate-spin border-t-orange-500" />
        </div>
      </div>
    );
  }

  return (
    <>
    <RegistrationLayout
      title="Conference Registration"
      subtitle={eventData?.event_title}
      onBack={onBackToTypeSelection ?? handleClose}
      backLabel={onBackToTypeSelection ? "Back to registration type" : "Back to Event"}
      backIcon={ChevronLeft}
      onBackToEvent={onBackToTypeSelection ? handleClose : undefined}
      backToEventLabel="Back to event"
      theme="orange"
      steps={steps}
      currentStep={displayStep}
      onStepClick={handleStepClick}
      footer={
        <div className="flex justify-between items-center">
          <motion.button
            onClick={handleBack}
            disabled={(currentStep === 0 && !onBackToTypeSelection) || (showPaymentChoice && !registrationData)}
            whileHover={(currentStep > 0 || (currentStep === 0 && onBackToTypeSelection)) && !showPaymentChoice ? { scale: 1.02 } : {}}
            whileTap={(currentStep > 0 || (currentStep === 0 && onBackToTypeSelection)) && !showPaymentChoice ? { scale: 0.98 } : {}}
            className={`px-6 py-3 text-sm font-semibold rounded-lg transition-all ${
              (currentStep === 0 && !onBackToTypeSelection) || showPaymentChoice
                ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                : "text-gray-700 bg-gray-100 hover:bg-gray-200"
            }`}
          >
            {currentStep === 0 && onBackToTypeSelection ? "Back to registration type" : "Back"}
          </motion.button>

          {!showPaymentChoice && (
            <motion.button
              onClick={handleNext}
              disabled={
                isSubmitting ||
                (currentStep === 0 &&
                  formData.hasReferralCode === true &&
                  (!formData.validatedReferralCode || referralValidationState.isValidating))
              }
              whileHover={
                !isSubmitting &&
                !(
                  currentStep === 0 &&
                  formData.hasReferralCode === true &&
                  (!formData.validatedReferralCode || referralValidationState.isValidating)
                )
                  ? { scale: 1.02 }
                  : {}
              }
              whileTap={
                !isSubmitting &&
                !(
                  currentStep === 0 &&
                  formData.hasReferralCode === true &&
                  (!formData.validatedReferralCode || referralValidationState.isValidating)
                )
                  ? { scale: 0.98 }
                  : {}
              }
              className="px-8 py-3 text-base font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-md transition-all hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex gap-2 items-center">
                  <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
                  <span>Submitting...</span>
                </div>
              ) : currentStep === (hasMotivationQuestions ? 3 : 2) ? (
                "Submit Registration"
              ) : (
                "Continue"
              )}
            </motion.button>
          )}
        </div>
      }
    >
      <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
    </RegistrationLayout>

      {/* Exit Confirmation Modal */}
      <AnimatePresence>
        {showExitModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex fixed inset-0 z-50 justify-center items-center backdrop-blur-sm bg-black/60"
              onClick={handleCancelExit}
            >
              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                onClick={(e) => e.stopPropagation()}
                className="overflow-hidden relative mx-4 w-full max-w-md bg-white rounded-2xl shadow-2xl"
              >
                {/* Decorative gradient header */}
                <div className="absolute top-0 right-0 left-0 h-2 bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500" />

                {/* Close button */}
                <button
                  onClick={handleCancelExit}
                  className="flex absolute top-4 right-4 z-10 justify-center items-center w-8 h-8 text-gray-400 bg-gray-100 rounded-full transition-all hover:text-gray-600 hover:bg-gray-200"
                >
                  <X size={18} />
                </button>

                {/* Content */}
                <div className="p-8 pt-12">
                  {/* Icon */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                    className="flex justify-center mb-6"
                  >
                    <div className="flex justify-center items-center w-20 h-20 bg-orange-100 rounded-full">
                      <AlertTriangle size={40} className="text-orange-600" />
                    </div>
                  </motion.div>

                  {/* Title */}
                  <motion.h3
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mb-3 text-2xl font-bold text-center text-gray-900"
                  >
                    Leave Registration?
                  </motion.h3>

                  {/* Message */}
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mb-8 leading-relaxed text-center text-gray-600"
                  >
                    Are you sure you want to leave? Your progress will be lost and you'll need to start over.
                  </motion.p>

                  {/* Buttons */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="flex gap-3"
                  >
                    <motion.button
                      onClick={handleCancelExit}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1 px-6 py-3 text-base font-semibold text-gray-700 bg-gray-100 rounded-xl transition-all hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
                    >
                      Continue Registration
                    </motion.button>
                    <motion.button
                      onClick={handleConfirmExit}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1 px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-md transition-all hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                    >
                      Leave Page
                    </motion.button>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

